import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import {
  createTemplate,
  getGetTemplateListQueryKey,
  getTemplate,
} from "~/api/custom-reports/custom-reports";
import type { SectionInput } from "~/api/model";
import { useToast } from "~/hooks/use-toast";

interface DuplicateTemplateArgs {
  organizationId: string;
  templateId: string;
}

export function useDuplicateTemplate() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const intl = useIntl();

  const mutation = useMutation({
    mutationFn: async ({
      organizationId,
      templateId,
    }: DuplicateTemplateArgs) => {
      const template = await getTemplate(organizationId, templateId);

      const newName = `${template.name} Copy`;
      const sections = (template.sections ?? []).map(
        ({ title, purpose, prompt, contextType, ...rest }) => {
          if (contextType === "docList") {
            return {
              title,
              purpose: "List all document titles by category",
              prompt: "docList",
              contextType: "docList" as const,
            };
          }
          if (contextType === "chronology") {
            const cfg = (rest as { config?: { prompt?: string } }).config;
            return {
              title,
              purpose: "Complete date-ordered chronology of all case events",
              prompt: "chronology",
              contextType: "chronology" as const,
              ...(cfg?.prompt ? { config: { prompt: cfg.prompt } } : {}),
            };
          }
          return {
            title,
            purpose,
            prompt,
            contextType: contextType ?? "snapshot",
          };
        }
        // SectionInputConfig is generated without config.prompt (stale spec);
        // the API accepts it, so we cast here until the spec is updated.
      ) as SectionInput[];

      return createTemplate(organizationId, {
        name: newName,
        definition: template.definition ?? "",
        sections,
      });
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: getGetTemplateListQueryKey(variables.organizationId),
      });
    },
    onError: () => {
      toast({
        title: intl.formatMessage({
          id: "template.duplicate.error",
          defaultMessage: "Failed to duplicate template",
        }),
      });
    },
  });

  return mutation;
}
